import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import DateRangeCalendar from '../components/DateRangeCalendar';

function FunnelPage({ showToast }) {
  const [periodType, setPeriodType] = useState('preset');
  const [periodDays, setPeriodDays] = useState(1);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);

  const [uniqueDaily, setUniqueDaily] = useState(true);

  const [funnel, setFunnel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const initialLoadDone = useRef(false);

  // Детализация по шагу
  const [selectedStep, setSelectedStep] = useState(null);
  const [stepUsers, setStepUsers] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsTotal, setDetailsTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getApiParams = useCallback(() => {
    const base = {};
    if (periodType === 'custom' && customFrom && customTo) {
      base.dateFrom = customFrom;
      base.dateTo = customTo;
    } else {
      const to = new Date();
      const from = new Date();
      // Сегодня (1) = только сегодня, 7 дней = сегодня + 6 предыдущих
      from.setDate(from.getDate() - (periodDays - 1));
      base.dateFrom = formatDate(from);
      base.dateTo = formatDate(to);
    }
    if (uniqueDaily) base.uniqueDaily = true;
    return base;
  }, [periodType, periodDays, customFrom, customTo, uniqueDaily]);

  const loadFunnel = useCallback(async () => {
    if (periodType === 'custom' && (!customFrom || !customTo)) return;
    setIsLoading(true);
    try {
      const data = await api.getAnalyticsFunnel(getApiParams());
      setFunnel(data);
      // При первой загрузке — автоматически открыть "Открыли витрину"
      if (!initialLoadDone.current) {
        initialLoadDone.current = true;
        setSelectedStep('catalog_opened');
      }
    } catch (error) {
      showToast('Ошибка загрузки воронки', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [getApiParams, periodType, customFrom, customTo, uniqueDaily, showToast]);

  useEffect(() => {
    loadFunnel();
  }, [loadFunnel]);

  const handlePreset = (days) => {
    setPeriodType('preset');
    setPeriodDays(days);
    setSelectedStep(null);
  };

  const handleCustomRange = (from, to) => {
    setCustomFrom(from);
    setCustomTo(to);
    setPeriodType('custom');
    setSelectedStep(null);
    setShowCalendar(false);
  };

  const loadDetails = useCallback(async (step) => {
    setDetailsLoading(true);
    try {
      const params = getApiParams();
      const data = await api.getAnalyticsFunnelDetails({ step, ...params, offset: 0 });
      setStepUsers(data.users || []);
      setDetailsTotal(data.total || 0);
      setHasMore(data.has_more || false);
    } catch (error) {
      showToast('Ошибка загрузки деталей', 'error');
    } finally {
      setDetailsLoading(false);
    }
  }, [getApiParams, showToast]);

  // Перезагрузить детализацию при смене режима или при установке selectedStep
  useEffect(() => {
    if (selectedStep) {
      loadDetails(selectedStep);
    }
  }, [uniqueDaily, selectedStep]); // eslint-disable-line

  const handleStepClick = async (step) => {
    setSelectedStep(step);
    loadDetails(step);
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const params = getApiParams();
      const data = await api.getAnalyticsFunnelDetails({
        step: selectedStep,
        ...params,
        offset: stepUsers.length,
      });
      setStepUsers(prev => [...prev, ...(data.users || [])]);
      setHasMore(data.has_more || false);
    } catch (error) {
      showToast('Ошибка загрузки', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const steps = funnel?.steps || [];
  const maxUsers = steps.length > 0 ? Math.max(...steps.map(s => s.users), 1) : 1;

  // Этапы воронки — одна зелёная шкала от светлого к тёмному, а не радуга (UX-аудит 25.10)
  const stepColors = {
    catalog_opened: '#9FD3B5',
    cart_added: '#7FC29C',
    cart_opened: '#5FAF84',
    checkout_started: '#4A8C6E',
    address_entered: '#2F6E52',
    order_created: '#245640',
    order_paid: '#1C3D2E',
  };

  const fmtDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
  };

  return (
    <div>
      {/* Период */}
      <div style={{
        background: 'white', '--ch': '10px', padding: '16px',
        marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {/* Период — первая строка */}
        {/* Период и режим — общие переключатели .seg, как в «Статистике» (UX-аудит 25.10) */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
          <div className="seg">
          {[
            { label: 'Сегодня', days: 1 },
            { label: '7 дней', days: 7 },
            { label: '14 дней', days: 14 },
            { label: '30 дней', days: 30 },
          ].map(p => (
            <button
              key={p.days}
              type="button"
              onClick={() => handlePreset(p.days)}
              className={`seg-btn${periodType === 'preset' && periodDays === p.days ? ' on' : ''}`}
            >
              {p.label}
            </button>
          ))}
          </div>

          <div style={{ position: 'relative' }}>
            <div className="seg">
            <button
              type="button"
              onClick={() => setShowCalendar(!showCalendar)}
              className={`seg-btn${periodType === 'custom' ? ' on' : ''}`}
            >
              {periodType === 'custom' && customFrom && customTo
                ? `${fmtDisplayDate(customFrom)} — ${fmtDisplayDate(customTo)}`
                : 'Выбрать период'}
            </button>
            </div>

            {showCalendar && (
              <DateRangeCalendar
                dateFrom={customFrom}
                dateTo={customTo}
                onSelect={handleCustomRange}
                onClose={() => setShowCalendar(false)}
              />
            )}
          </div>
        </div>

        {/* Режим подсчёта — вторая строка */}
        <div className="seg">
          <button type="button" onClick={() => setUniqueDaily(true)} className={`seg-btn${uniqueDaily ? ' on' : ''}`}>
            Уникальные за день
          </button>
          <button type="button" onClick={() => setUniqueDaily(false)} className={`seg-btn${!uniqueDaily ? ' on' : ''}`}>
            Все события
          </button>
        </div>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
          Загрузка...
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '20px', alignItems: 'stretch', flexWrap: 'wrap' }}>
          {/* flexWrap: на телефоне воронка и детали — друг под другом (в 1/3 ширины цифры обрезались);
              базы 220/380 — на планшете и мониторе 1024 остаются рядом */}
          {/* Левая часть — воронка (1/3) */}
          <div style={{
            flex: '1 1 220px', maxWidth: '100%', minWidth: 0,
            background: 'white', '--ch': '10px', padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            {steps.map((step, i) => {
              const barWidth = Math.max((step.users / maxUsers) * 100, 8);
              const isSelected = selectedStep === step.step;

              return (
                <div
                  key={step.step}
                  onClick={() => handleStepClick(step.step)}
                  style={{
                    marginBottom: i < steps.length - 1 ? '4px' : 0,
                    cursor: 'pointer',
                    padding: '8px 10px',
                    '--ch': '6px', '--stroke': (isSelected ? 'var(--primary-color)' : 'transparent'),
                    background: isSelected ? '#F1F6F3' : 'transparent',
                    border: isSelected ? '1px solid var(--primary-color)' : '1px solid transparent',
                    transition: 'all 0.2s, --stroke 0.2s',
                  }}
                >
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: '4px'
                  }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>
                      {step.label}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '16px', fontWeight: 700, color: '#333' }}>
                        {step.users}
                      </span>
                      {step.conversion !== null && (
                        <span style={{
                          fontSize: '11px', fontWeight: 600,
                          color: step.conversion >= 50 ? '#359250' : step.conversion >= 25 ? '#D3A842' : '#CB4D45',
                          background: step.conversion >= 50 ? '#DCF4E0' : step.conversion >= 25 ? '#F9EBCF' : '#FFE6E3',
                          padding: '1px 6px', '--ch': '8px',
                        }}>
                          {step.conversion}%
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{
                    height: '20px', background: '#F4F4EF', '--ch': '4px', overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${barWidth}%`,
                      height: '100%',
                      background: stepColors[step.step] || '#666',
                      '--ch': '4px',
                      transition: 'width 0.5s ease',
                      opacity: 0.85,
                    }} />
                  </div>

                  {i < steps.length - 1 && step.users > 0 && (
                    <div style={{
                      textAlign: 'center', margin: '2px 0 -2px',
                      fontSize: '10px', color: '#ccc',
                    }}>
                      ↓
                    </div>
                  )}
                </div>
              );
            })}

            {steps.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: '13px' }}>
                Нет данных за выбранный период
              </div>
            )}
          </div>

          {/* Правая часть — список пользователей (2/3) */}
          <div style={{
            flex: '2 1 380px', minWidth: 0,
            background: 'white', '--ch': '10px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column',
          }}>
            {!selectedStep ? (
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#bbb', fontSize: '14px',
              }}>
                Выберите шаг воронки слева
              </div>
            ) : (
              <>
                <div style={{
                  padding: '14px 16px', borderBottom: '1px solid #EBEDEC',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
                    {steps.find(s => s.step === selectedStep)?.label}
                  </h3>
                  <span style={{ fontSize: '12px', color: '#999' }}>
                    {detailsTotal} {uniqueDaily ? 'чел.' : 'событий'}
                  </span>
                </div>

                {detailsLoading ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                    Загрузка...
                  </div>
                ) : stepUsers.length === 0 ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                    Нет данных
                  </div>
                ) : (
                  <div style={{ flex: 1, overflow: 'auto' }}>
                    <div style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 0,
                    }}>
                      {stepUsers.map((user, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '8px 12px',
                            borderBottom: '1px solid #F4F4EF',
                            borderRight: idx % 3 !== 2 ? '1px solid #F4F4EF' : 'none',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: '8px',
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: 500, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {user.username ? (
                                <a
                                  href={`https://t.me/${user.username}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#3A83BB', textDecoration: 'none' }}
                                >
                                  @{user.username}
                                </a>
                              ) : (
                                [user.first_name, user.last_name].filter(Boolean).join(' ') || `ID ${user.telegram_id}`
                              )}
                            </div>
                            {user.username && (user.first_name || user.last_name) && (
                              <div style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {[user.first_name, user.last_name].filter(Boolean).join(' ')}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap' }}>
                              {user.last_at ? new Date(user.last_at).toLocaleString('ru-RU', {
                                day: '2-digit', month: '2-digit',
                                hour: '2-digit', minute: '2-digit',
                              }) : '—'}
                            </div>
                            {uniqueDaily && user.event_count > 1 && (
                              <div style={{ fontSize: '10px', color: 'var(--accent-color)', fontWeight: 600 }}>
                                {user.event_count}x
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {hasMore && (
                      <div style={{ textAlign: 'center', padding: '12px' }}>
                        <button type="button" className="btn btn-secondary btn-sm"
                          onClick={handleLoadMore}
                          disabled={loadingMore}
                        >
                          {loadingMore ? 'Загрузка...' : 'Загрузить ещё'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FunnelPage;
